const displayStandard = item => item.standard || item.standard_display || [item.standard_system, item.standard_number].filter(Boolean).join(" ");

const displayLength = item => item.length || (item.length_mm == null ? null : `${item.length_mm}mm`);

const displayPacking = item => item.packing || (item.pack_quantity ? `${item.pack_quantity} pcs/Box` : null);

const strictBucket = item => item.candidate_category === "likely_intended" ? "likely_intended" : item.candidate_category === "alternative" ? "alternatives" : item.technical_status === "CODE_FOUND" && item.identity_status === "VERIFIED"
  ? "exact"
  : item.technical_status === "VERIFIED_TECHNICAL_MATCH" && item.can_show_green_badge === true
  ? "exact"
  : item.technical_status === "MISMATCH" ? "alternatives" : "variants";

const normalizeItem = item => ({
  ...item,
  article_id: item.article_id || item.id,
  sku: item.sku || item.article_number || item.aramfix_article_number,
  name: item.name || item.title || item.primary_name,
  canonical_path: item.canonical_path || item.url,
  standard: displayStandard(item),
  diameter: item.diameter || item.nominal_diameter,
  length: displayLength(item),
  material_grade: item.material_grade || item.property_class || item.material_grade_normalized,
  surface: item.surface || item.surface_variant || item.surface_family || item.surface_system,
  packing: displayPacking(item),
  image_url: item.image_url || item.primary_image_url || item.image_urls?.[0],
  stock_indicator: item.stock_indicator || (Number(item.total_stock || 0) > 0 ? "in_stock" : "unknown"),
  match_reason: item.match_reason || item.explanation || "",
  field_comparison: item.field_comparison || item.field_checks || [],
  technical_differences: item.technical_differences || (item.field_comparison || item.field_checks || []).filter(row => ["MISMATCH", "DEVIATION", "CONFLICT"].includes(row.status)).map(row => ({ field: row.field, message: `${row.label}: ${row.actual || "—"} — gevraagd ${row.requested || "—"}` })),
  variant_differences: item.variant_differences || (item.field_comparison || item.field_checks || []).filter(row => ["NOT_ASKED", "UNKNOWN", "UNKNOWN_DATA"].includes(row.status)).map(row => ({ field: row.field, label: row.label })),
  bucket: strictBucket(item),
});

const strictGroups = items => ({
  exact: items.filter(item => item.bucket === "exact"),
  likely_intended: items.filter(item => item.bucket === "likely_intended"),
  variants: items.filter(item => item.bucket === "variants"),
  alternatives: items.filter(item => item.bucket === "alternatives"),
});

const groupItems = payload => (payload?.groups || []).flatMap(group => group?.items || []);

export const normalizeSearchPayload = payload => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) throw new TypeError("Ongeldig zoekresponsecontract");
  const currentBuckets = payload.buckets;
  if (Array.isArray(currentBuckets?.exact?.items) && Array.isArray(currentBuckets?.variants?.items) && Array.isArray(currentBuckets?.alternatives?.items)) {
    const grouped = strictGroups([...currentBuckets.exact.items, ...(currentBuckets.likely_intended?.items || []), ...currentBuckets.variants.items, ...currentBuckets.alternatives.items].map(normalizeItem));
    const { exact, likely_intended, variants, alternatives } = grouped;
    const inferredTotal = exact.length + likely_intended.length + variants.length;
    const items = [...exact, ...likely_intended, ...variants];
    const facets = payload.facets || {};
    if (!Object.keys(facets).length) {
      for (const [key, field] of [["standard", "standard"], ["diameter", "diameter"], ["length", "length"], ["grade", "material_grade"], ["surface", "surface"], ["packing", "packing"]]) facets[key] = [...new Set(items.map(item => item[field]).filter(Boolean))].sort();
    }
    return {
      ...payload,
      total: Number(payload.total || inferredTotal),
      response_ms: Number(payload.response_ms || 0),
      facets,
      buckets: {
        exact: { ...currentBuckets.exact, count: Number(currentBuckets.exact.count ?? exact.length), items: exact },
        likely_intended: { ...(currentBuckets.likely_intended || {}), count: Number(currentBuckets.likely_intended?.count ?? likely_intended.length), items: likely_intended },
        variants: { ...currentBuckets.variants, count: Number(currentBuckets.variants.count ?? variants.length), items: variants },
        alternatives: { ...currentBuckets.alternatives, count: Number(currentBuckets.alternatives.count ?? alternatives.length), items: alternatives },
      },
    };
  }

  const exactSource = payload.exact_results || currentBuckets?.choice_required?.items || currentBuckets?.exact?.items || [];
  const variantSource = currentBuckets?.technically_fitting?.items || [];
  const alternativeSource = payload.controlled_alternatives || currentBuckets?.controlled_alternatives?.items || [];
  const flatSource = payload.items?.length ? payload.items : groupItems(payload);
  const candidateSource = exactSource.length || variantSource.length || alternativeSource.length ? [...exactSource, ...variantSource, ...alternativeSource] : flatSource;
  const { exact, likely_intended, variants, alternatives } = strictGroups(candidateSource.map(normalizeItem));
  const items = [...exact, ...likely_intended, ...variants];
  const facets = payload.facets || {};
  if (!Object.keys(facets).length) {
    for (const [key, field] of [["standard", "standard"], ["diameter", "diameter"], ["length", "length"], ["grade", "material_grade"], ["surface", "surface"], ["packing", "packing"]]) facets[key] = [...new Set(items.map(item => item[field]).filter(Boolean))].sort();
  }
  const understanding = payload.query_understanding || {};
  return {
    ...payload,
    total: Number(payload.total ?? payload.total_catalog_matches ?? items.length),
    response_ms: Number(payload.response_ms || 0),
    page: Number(payload.page || 1), pages: Number(payload.pages || 1), facets,
    intent: payload.intent || { constraints: understanding.filter_chips || understanding.constraints || [], confidence_percentage: Math.round(Number(understanding.interpretation_confidence || 0) * 100) },
    ai_used: payload.ai_used ?? payload.ai_called ?? false,
    buckets: {
      exact: { count: exact.length, items: exact },
      likely_intended: { count: likely_intended.length, items: likely_intended },
      variants: { count: variants.length, items: variants },
      alternatives: { count: alternatives.length, items: alternatives },
    },
  };
};